import { useEffect, useSyncExternalStore } from "react";
import { de } from "./de";
import { en } from "./en";

export type WorkspaceLanguage = "de" | "en";

declare const window: {
  location: { pathname: string };
  localStorage: { getItem(key: string): string | null; setItem(key: string, value: string): void };
  dispatchEvent(event: Event): boolean;
  addEventListener(type: string, listener: (...args: never[]) => void): void;
  removeEventListener(type: string, listener: (...args: never[]) => void): void;
} | undefined;
declare const document: { documentElement: { lang: string } } | undefined;
declare const CustomEvent: { new(type: string, eventInitDict?: { detail?: unknown }): Event } | undefined;

export const translations = { de, en } as const;

const LANGUAGE_STORAGE_KEY = "hauffe-os-workspace-language";
const LANGUAGE_CHANGED_EVENT = "hauffe-os-language-changed";
const DEFAULT_LANGUAGE: WorkspaceLanguage = "de";

function isWorkspaceLanguage(value: string | null | undefined): value is WorkspaceLanguage {
  return value === "de" || value === "en";
}

function readStoredLanguage(): WorkspaceLanguage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isWorkspaceLanguage(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

function writeDocumentLanguage(language: WorkspaceLanguage): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = language;
  }
}

export function languageFromPathname(pathname: string): WorkspaceLanguage {
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  return firstSegment === "en" ? "en" : "de";
}

export function stripLanguagePrefix(pathname: string): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (normalized === "/en") return "/";
  if (normalized.startsWith("/en/")) return normalized.slice(3) || "/";
  return normalized;
}

export function withLanguagePrefix(pathname: string, language: WorkspaceLanguage): string {
  const normalized = stripLanguagePrefix(pathname || "/");
  if (language === "en") return normalized === "/" ? "/en" : `/en${normalized}`;
  return normalized;
}

export function switchLanguagePath(currentPath: string, targetLanguage: WorkspaceLanguage): string {
  const url = new URL(currentPath, "https://app.hauffe.io");
  return `${withLanguagePrefix(url.pathname, targetLanguage)}${url.search}${url.hash}`;
}

export function getWorkspaceLanguage(): WorkspaceLanguage {
  if (typeof window !== "undefined" && window?.location?.pathname) {
    return languageFromPathname(window.location.pathname);
  }
  return readStoredLanguage() ?? DEFAULT_LANGUAGE;
}

export function setWorkspaceLanguage(language: WorkspaceLanguage): void {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // Browser storage can be unavailable in restricted contexts; the current render still updates via the event.
    }
    if (typeof CustomEvent !== "undefined") {
      window.dispatchEvent(new CustomEvent(LANGUAGE_CHANGED_EVENT, { detail: language }));
    } else {
      window.dispatchEvent(new Event(LANGUAGE_CHANGED_EVENT));
    }
  }
  writeDocumentLanguage(language);
}

export function subscribeWorkspaceLanguage(listener: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handleStorage = (event: { key?: string | null }) => {
    if (event.key === LANGUAGE_STORAGE_KEY) listener();
  };
  window.addEventListener(LANGUAGE_CHANGED_EVENT, listener);
  window.addEventListener("storage", handleStorage);
  window.addEventListener("popstate", listener);
  return () => {
    window.removeEventListener(LANGUAGE_CHANGED_EVENT, listener);
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener("popstate", listener);
  };
}

export function useWorkspaceLanguage(): WorkspaceLanguage {
  const language = useSyncExternalStore(subscribeWorkspaceLanguage, getWorkspaceLanguage, () => DEFAULT_LANGUAGE);

  useEffect(() => {
    writeDocumentLanguage(language);
  }, [language]);

  return language;
}

export function t(language: WorkspaceLanguage = "de") {
  return translations[language];
}
